/**
 * Hiring Manager Intake (People → Talent Acquisition) — contratos entre
 * backend y frontend. Spec: "Hiring Manager Intake Agent — Especificación
 * técnica" v2.0 (21/09/2026), secciones 2 (estados), 5 (herramientas) y 6
 * (esquemas). Las reglas puras viven en shared/ta-intake.ts.
 *
 * Dos documentos por búsqueda: la Job Description (la valida el HM) y la
 * Sourcing Guide (interna de TA — el HM NUNCA la ve: los tipos de la vista del
 * HM no tienen un campo donde pueda viajar).
 */

/** Estados de una sesión (spec §2). SAVED → IN_PROCESS lo marca el recruiter; CLOSED lo cierra TA. */
export const TA_INTAKE_STATUSES = [
  'INTERVIEWING', 'JD_REVIEW', 'JD_REVISING', 'JD_APPROVED', 'SAVED', 'IN_PROCESS', 'CLOSED', 'ABANDONED',
] as const;
export type TaIntakeStatus = (typeof TA_INTAKE_STATUSES)[number];

// ─── Esquemas de datos (spec §6) ─────────────────────────────────────────────

export interface LanguageReq {
  language: string;
  level: string;
  /** true = excluyente, false = deseable */
  required: boolean;
}

/** Una versión de la JD en un idioma. */
export interface JDVersionDoc {
  job_title: string;
  area_team: string;
  /** Nombre del puesto del líder, nunca el de una persona. */
  reports_to: string;
  impact_level: string;
  location_and_work_model: string;
  /** 2–3 párrafos. */
  overview_paragraphs: string[];
  /** 6–8 bullets con verbo de acción. */
  responsibilities: string[];
  /** ≥ 4. */
  requirements: string[];
}

export interface JobDescription {
  esp: JDVersionDoc;
  eng: JDVersionDoc;
}

export interface CompanyGroup {
  universe: string;
  companies: string[];
  why: string;
}

export interface JobTitlesByLang {
  en: string[];
  es: string[];
  pt: string[];
}

export interface SourcingGuide {
  target_profile: string[];
  must_have: string[];
  nice_to_have: string[];
  job_titles: { primary: JobTitlesByLang; alternative: JobTitlesByLang; notes?: string };
  keywords: { skills: string[]; industry_business_model: string[]; tools_platforms: string[]; stakeholders_clients: string[] };
  target_companies: { hm_mentioned: string[]; direct: CompanyGroup[]; extended: CompanyGroup[] };
  linkedin_filters: Array<{ filter: string; recommendation: string }>;
  boolean_searches: Array<{ name: 'Preciso' | 'Ampliado' | 'Alternativo'; query: string; note?: string }>;
  profile_signals: string[];
  red_flags: string[];
  search_strategy: string[];
}

export type WorkModel = 'Remoto' | 'Híbrido' | 'Presencial';

export interface IntakeSummary {
  job_title: string;
  area_team: string;
  reports_to: string;
  /** 'Pendiente' solo si el HM lo declaró explícitamente. */
  impact_level: string;
  locations: string[];
  work_model: WorkModel;
  /** Oficina y frecuencia. Obligatorio si Híbrido o Presencial. */
  office_attendance?: string;
  languages: LanguageReq[];
  team_size_managed?: number;
  opening_reason?: string;
  /** Contexto del HM que no entra en la JD (anti-perfil, etc.). */
  hm_notes?: string;
}

// ─── Sesión ──────────────────────────────────────────────────────────────────

export type TaIntakeJdStatus = 'proposed' | 'approved' | 'superseded';

export interface TaIntakeJdVersion {
  /** 'v1', 'v2'… (es el `jd_version_id` que ve el modelo). */
  id: string;
  n: number;
  /** Una línea con qué cambió ('Versión inicial' en la primera). */
  note: string;
  status: TaIntakeJdStatus;
  at: string;
  esp: JDVersionDoc;
  eng: JDVersionDoc;
}

/** Fila del repositorio de búsquedas (spec §7) — lo que listan HM y recruiter. */
export interface TaIntakeSessionSummary {
  sessionId: string;
  status: TaIntakeStatus;
  hmEmail: string;
  hmName: string;
  /** Vacío hasta la primera JD. */
  jobTitle: string;
  areaTeam: string;
  createdAt: string;
  updatedAt: string;
  approvedAt: string | null;
  savedAt: string | null;
  /** Rondas de cambios antes de aprobar (versiones − 1). */
  jdRevisions: number;
  jdVersions: number;
  /** Turnos del HM. */
  turns: number;
  assignedRecruiter: string | null;
  /** Texto legible de idiomas del resumen del intake (solo tras guardar). */
  languagesLabel: string;
}

/** Un mensaje de la conversación como lo ve la pantalla (nunca los bloques crudos). */
export interface TaIntakeMessageView {
  id: string;
  role: 'user' | 'assistant';
  at: string;
  text: string;
  /** Acción del HM con los botones (se dibuja como chip, no como texto). */
  action?: { kind: 'approved' | 'changes_requested'; jdVersionId: string; comments?: string };
  /** El agente presentó esta versión de la JD en este mensaje. */
  jdVersionId?: string;
}

/** Lo que ve el Hiring Manager de su sesión. SIN Sourcing Guide por construcción. */
export interface TaIntakeHmView {
  session: TaIntakeSessionSummary;
  messages: TaIntakeMessageView[];
  jdVersions: TaIntakeJdVersion[];
  /** Versión sobre la que el HM puede Aprobar / Pedir cambios. */
  pendingJdVersionId: string | null;
  approvedJdVersionId: string | null;
}

/**
 * Lo que ve el recruiter (spec §3): la JD y sus versiones + la guía + el resumen.
 *
 * **Sin `messages`**: TA pidió que las conversaciones del intake no queden
 * guardadas (pregunta 14, 29/09/2026) y Sol lo confirmó el 30/09. La
 * transcripción no se muestra Y TAMPOCO SE MANDA: sacarla del tipo es lo que
 * garantiza que no viaje al front de TA aunque la búsqueda esté a mitad de
 * entrevista. El HM sigue viendo su propio chat (`TaIntakeHmView`).
 */
export interface TaIntakeDetail extends Omit<TaIntakeHmView, 'messages'> {
  guide: SourcingGuide | null;
  intakeSummary: IntakeSummary | null;
}

export type TaIntakeTurnInput =
  | { kind: 'text'; text: string }
  | { kind: 'approve'; jdVersionId: string }
  | { kind: 'changes'; jdVersionId: string; comments: string };

export interface TaIntakeTurnResult {
  view: TaIntakeHmView;
  latencyMs: number;
}

/** Estados que el recruiter puede fijar desde la lista. */
export const TA_INTAKE_RECRUITER_STATUSES = ['SAVED', 'IN_PROCESS', 'CLOSED'] as const;
export type TaIntakeRecruiterStatus = (typeof TA_INTAKE_RECRUITER_STATUSES)[number];
